import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useCustomerAuth } from './contexts/CustomerAuthContext'
import { CustomerLayout } from './components/layout/CustomerLayout'
import { LoadingScreen } from './components/common/LoadingScreen'
import { ErrorBoundary } from './components/common/ErrorBoundary'

// Pages
import { LandingPage } from './pages/LandingPage'
import { Login } from './pages/Login'
import { PasswordSetup } from './pages/PasswordSetup'
import { AccessPage } from './pages/AccessPage'
import { Dashboard } from './pages/Dashboard'
import { JobList } from './pages/JobList'
import { JobDetails } from './pages/JobDetails'
import { EstimateList } from './pages/EstimateList'
import { EstimateDetails } from './pages/EstimateDetails'
import { InvoiceList } from './pages/InvoiceList'
import { InvoiceDetails } from './pages/InvoiceDetails'
import { InvoicePayment } from './pages/InvoicePayment'
import { PaymentSuccess } from './pages/PaymentSuccess'
import { DocumentUpload } from './pages/DocumentUpload'
import { Profile } from './pages/Profile'
import { VisualizerDashboard } from './pages/VisualizerDashboard'
import ProductSearch from './pages/ProductSearch'
import { FormEntry } from './pages/FormEntry'
import { FormComplete } from './pages/FormComplete'
import { PublicEstimatePdf } from './pages/PublicEstimatePdf'
import { PublicInvoicePdf } from './pages/PublicInvoicePdf'
import { TechnicianTracking } from './pages/TechnicianTracking'
import { ReviewFeedback } from './pages/ReviewFeedback'
import { ReviewThankYou } from './pages/ReviewThankYou'
import Subscriptions from './pages/Subscriptions'
import { StatementPayPage } from './pages/StatementPayPage'
import { PaymentMethodSelectPage } from './pages/PaymentMethodSelectPage'
import { PublicPhotoDocs } from './pages/PublicPhotoDocs'
import CustomerPortalNoIndex from './components/seo/CustomerPortalNoIndex'

function App() {
  const { isAuthenticated, isLoading, customer, portalConfig } = useCustomerAuth()
  const location = useLocation()
  
  // Get default landing page from portal config
  const defaultLandingPage = portalConfig?.defaultLandingPage || '/dashboard'

  // Show loading screen while checking authentication
  // BUT: Don't block public form routes, public PDF routes, tracking routes, or review routes from loading
  const isPublicRoute = location.pathname.startsWith('/forms/') || location.pathname.startsWith('/pdf/') || location.pathname.startsWith('/estimates/') || location.pathname.startsWith('/tracking/') || location.pathname.startsWith('/review/') || location.pathname.startsWith('/photo-docs/') || location.pathname === '/pay' || location.pathname.startsWith('/pay/select/')
  if (isLoading && !isPublicRoute) {
    return <LoadingScreen message="Loading your portal..." />
  }

  return (
    <ErrorBoundary>
      <CustomerPortalNoIndex />
      <Routes>
        {/* Public routes - always available (must be defined first) */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/access/:token" element={<AccessPage />} />
        
        {/* Public form routes - must be before catch-all routes */}
        <Route path="/forms/:token" element={<FormEntry />} />
        <Route path="/forms/:token/complete" element={<FormComplete />} />
        
        {/* Public PDF routes - accessible via time-limited tokens */}
        <Route path="/pdf/estimate/:token" element={<PublicEstimatePdf />} />
        <Route path="/pdf/invoice/:token" element={<PublicInvoicePdf />} />
        
        {/* Public technician tracking route - accessible via time-limited tokens */}
        <Route path="/tracking/:token" element={<TechnicianTracking />} />

        {/* Public job photo documentation share links */}
        <Route path="/photo-docs/:token" element={<PublicPhotoDocs />} />
        
        {/* Public review feedback routes - accessible via time-limited tokens */}
        <Route path="/review/:token" element={<ReviewFeedback />} />
        <Route path="/review/:token/thank-you" element={<ReviewThankYou />} />
        
        {/* Public estimate routes with token authentication - always available */}
        <Route path="/estimates/:id/approve" element={<EstimateDetails />} />
        <Route path="/estimates/:id" element={<EstimateDetails />} />
        
        {/* Public statement pay page - from AR statement email link */}
        <Route path="/pay" element={<StatementPayPage />} />
        {/* Payment method selection - card vs ACH before generating payment link */}
        <Route path="/pay/select/:token" element={<PaymentMethodSelectPage />} />
        
        {/* Protected routes */}
        {isAuthenticated && customer ? (
          <>
            {/* Check if customer needs to set up password */}
            <Route path="/setup-password" element={<PasswordSetup />} />
            
            <Route path="/*" element={
              <CustomerLayout>
                <Routes>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/jobs" element={<JobList />} />
                  <Route path="/jobs/:jobId" element={<JobDetails />} />
                  <Route path="/jobs/:jobId/documents" element={<DocumentUpload />} />
                  <Route path="/estimates" element={<EstimateList />} />
                  <Route path="/invoices" element={<InvoiceList />} />
                  <Route path="/invoices/:invoiceId" element={<InvoiceDetails />} />
                  <Route path="/invoices/:invoiceId/pay" element={<InvoicePayment />} />
                  <Route path="/invoices/:invoiceId/payment-success" element={<PaymentSuccess />} />
                  <Route path="/products" element={<ProductSearch />} />
                  <Route path="/visualizer" element={<VisualizerDashboard />} />
                  <Route path="/subscriptions" element={<Subscriptions />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="*" element={<Navigate to={defaultLandingPage} replace />} />
                </Routes>
              </CustomerLayout>
            } />
          </>
        ) : (
          <>
            {/* Redirect other unauthenticated users to login - but NOT /forms/:token which is already handled above */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </>
        )}
      </Routes>
    </ErrorBoundary>
  )
}

export default App
